import * as yup from 'yup';

import {
  DAY_NUMBER_MAPPING,
  DAY_NUMBER_MAPPING_REVERSE,
} from 'features/hiringV2/constants';
import {
  JobRequest,
  JobRequestWeekAvailability,
} from 'features/hiringV2/interfaces/IJobRequest';

import { updateQuery } from 'util/router';

import { initialJobData, STAGE_NUMBER_MAPPING } from './constants';
import {
  JobData,
  JobDataErrors,
  JobFormStage,
  JobFormStep,
  JobRequirementsArray,
  StageToSteps,
} from './types';

// Validations
export const jobDescriptionSchema = yup.object().shape({
  job_role: yup.string().required('Job role is required'),
  job_requirements: yup.object().shape({
    type: yup.string().required('Job type is required'),
    days: yup.array().of(yup.string()).required('Job days are required'),
    shifts: yup.array().of(yup.string()).required('Job shifts are required'),
    years_of_experience: yup
      .string()
      .required('Job years of experience are required'),
  }),
  job_pay: yup.object().shape({
    range: yup
      .object()
      .shape({
        min: yup
          .number()
          .required('Minimum pay is required')
          .min(1, 'Minimum pay must be greater than 0'),
        max: yup.number(),
        frequency: yup.string().required('Job pay frequency is required'),
      })
      .required('Job pay is required')
      .test(
        'valid-range',
        'Minimum pay cannot be greater than maximum pay',
        value => {
          if (!value || !value.max) return true; // Let the required validation handle empty values
          return value.min <= value.max;
        }
      ),
  }),
});

export const isJobDetailStepComplete = (
  id: string,
  errors: JobDataErrors | undefined
) => {
  // Each accordion is dependent on the previous one
  const job_role_completed = !errors?.job_role;
  const job_requirements_completed =
    job_role_completed && !errors?.job_requirements;
  const job_pay_completed = job_requirements_completed && !errors?.job_pay;
  const more_info_completed = job_pay_completed && !errors?.more_info;

  switch (id) {
    case 'job_role':
      return job_role_completed;
    case 'job_requirements':
      return job_requirements_completed;
    case 'job_pay':
      return job_pay_completed;
    case 'more_info':
      return more_info_completed;
    default:
      return false;
  }
};

export const goToNextStage = <S extends JobFormStage>(
  stage: S,
  step?: StageToSteps[S]
) => {
  updateQuery(`stage`, stage);
  updateQuery(`step`, step);
};

const mapJobDataDaysAndShiftsToWeekAvailability = (
  days: string[],
  shifts: string[]
): JobRequestWeekAvailability => {
  const weekAvailabilities: JobRequestWeekAvailability = {
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
  };
  days.forEach(day => {
    const dayKey = DAY_NUMBER_MAPPING[day] as keyof JobRequestWeekAvailability;
    weekAvailabilities[dayKey] = shifts;
  });
  return weekAvailabilities;
};

const getJobDataDaysFromWeekAvailability = (
  weekAvailability: JobRequestWeekAvailability | undefined
): string[] => {
  if (!weekAvailability) return [];
  return (Object.keys(weekAvailability) as (keyof JobRequestWeekAvailability)[])
    .filter(day => Array.isArray(weekAvailability[day]))
    .filter(day => !!weekAvailability[day].length)
    .map(day => DAY_NUMBER_MAPPING_REVERSE[day]);
};

const getJobDataShiftsFromWeekAvailability = (
  weekAvailability: JobRequestWeekAvailability | undefined
): string[] => {
  if (!weekAvailability) return [];
  const firstDayWithShifts = (
    Object.keys(weekAvailability) as (keyof JobRequestWeekAvailability)[]
  ).find(day => weekAvailability[day].length > 0);
  if (!firstDayWithShifts) return [];
  return weekAvailability[firstDayWithShifts];
};

export const mapJobDataToJobRequest = (jobData: JobData): JobRequest => {
  const jobRequest: JobRequest = {
    id: jobData.id || 0,
    role_id: jobData.role_id,
  };
  jobRequest.role_name = jobData.job_role;
  jobRequest.title = jobData.job_title || jobData.job_role;
  jobRequest.employment_type = jobData.job_requirements.type;
  jobRequest.description = jobData.job_description;
  jobRequest.years_experience = jobData.job_requirements.years_of_experience;
  jobRequest.job_compensation = {
    tip_amount: jobData.job_pay?.tips?.amount,
    tip_frequency: jobData.job_pay?.tips?.time_range,
    target_wage_rate: jobData.job_pay?.range?.min,
    target_wage_rate_max: jobData.job_pay?.range?.max || null,
    wage_type: jobData.job_pay?.range?.frequency,
    comments: jobData.job_pay?.benefits,
  };
  jobRequest.builder_progress = jobData.builder_progress;
  jobRequest.syndication_settings = jobData.syndication_settings;
  jobRequest.week_availabilities = mapJobDataDaysAndShiftsToWeekAvailability(
    jobData.job_requirements.days,
    jobData.job_requirements.shifts
  );
  jobRequest.application_questions = jobData.application_questions;
  jobRequest.status = jobData.status;

  return jobRequest;
};

export const mapJobRequestToJobData = (jobRequest: JobRequest): JobData => {
  const jobData = {
    id: jobRequest.id,
    status: jobRequest.status,
  } as JobData;
  jobData.role_id = jobRequest.role_id;
  jobData.job_title = jobRequest.title || jobRequest.role_name || '';
  jobData.job_role = jobRequest.role_name || '';
  jobData.job_description = jobRequest.description || '';
  jobData.prompt = jobRequest.builder_progress?.prompt_body || '';
  jobData.job_requirements = {
    type: jobRequest.employment_type || '',
    years_of_experience: jobRequest.years_experience || '',
    days: getJobDataDaysFromWeekAvailability(jobRequest.week_availabilities),
    shifts: getJobDataShiftsFromWeekAvailability(
      jobRequest.week_availabilities
    ),
  };
  jobData.job_pay = {
    range: {
      min: jobRequest.job_compensation?.target_wage_rate || 0,
      max: jobRequest.job_compensation?.target_wage_rate_max || 0,
      frequency: jobRequest.job_compensation?.wage_type || '',
    },
    tips: {
      amount: jobRequest.job_compensation?.tip_amount || 0,
      time_range: jobRequest.job_compensation?.tip_frequency || '',
    },
    benefits: jobRequest.job_compensation?.comments || '',
  };
  jobData.more_info = jobRequest.description || '';
  jobData.builder_progress = jobRequest.builder_progress;
  jobData.syndication_settings =
    jobRequest.syndication_settings || initialJobData.syndication_settings;
  return jobData;
};

export const getStageNumber = (stage: JobFormStage | undefined) =>
  stage ? STAGE_NUMBER_MAPPING[stage] : 1;

export const getStepNumber = (
  stage: JobFormStage | undefined,
  step: JobFormStep | undefined
) => {
  if (!stage) return 1;
  if (!step) return 1;

  if (stage === 'job_description') {
    if (step === 'creating') return 2;
  }
  if (stage === 'screener') {
    if (step === 'questions') return 2;
  }
  if (stage === 'review_and_post') {
    if (step === 'job_review') return 1;
    if (step === 'screener') return 2;
    if (step === 'interview_times') return 3;
    if (step === 'review_business_info') return 4;
    if (step === 'job_boards') return 5;
  }
  return 1;
};

export const getStageFromNumber = (number: number | undefined) => {
  if (!number) return 'job_description';
  const stage = Object.keys(STAGE_NUMBER_MAPPING).find(
    key => STAGE_NUMBER_MAPPING[key as JobFormStage] === number
  );
  if (!stage) return 'job_description';
  return stage as JobFormStage;
};

export const getStepFromNumber = (
  stage: JobFormStage | undefined,
  number: number | undefined
): JobFormStep | undefined => {
  if (!stage) return undefined;
  if (!number) return undefined;

  if (stage === 'job_description') {
    if (number === 2) return 'creating';
    return undefined;
  }
  if (stage === 'screener') {
    if (number === 2) return 'questions';
    return 'review';
  }
  if (stage === 'review_and_post') {
    if (number === 1) return 'job_review';
    if (number === 2) return 'screener';
    if (number === 3) return 'interview_times';
    if (number === 4) return 'review_business_info';
    if (number === 5) return 'job_boards';
    return 'job_review';
  }
  return undefined;
};

export const jobRequirementValueIsArray = (
  key: string
): key is keyof JobRequirementsArray => key === 'days' || key === 'shifts';
